'use strict';

const LAZY_CLASS = 'lazy-loading';
const LOADED_CLASS = 'lazy-loaded';
const LAZY_SELECTOR = 'img.' + LAZY_CLASS;

// No margin: images start fetching only once they reach the viewport. Widening
// this buys earlier decodes at the cost of bytes that compete with the LCP image.
const ROOT_MARGIN = '0px 0px';

let imageObserver = null;
let started = false;

/**
 * Promotes the deferred attributes on a lazy image to their real counterparts.
 *
 * srcset and sizes are only filled in when absent: templates that already ship a
 * real srcset are authoritative, and overwriting it would discard a value the
 * browser has already begun acting on.
 *
 * @param {HTMLImageElement} img image to reveal
 */
function reveal(img) {
    const sizes = img.getAttribute('data-sizes');
    const srcset = img.getAttribute('data-srcset');
    const src = img.getAttribute('data-src');
    const parent = img.parentNode;

    // A <source> that matches wins over anything on the <img>, so the whole
    // <picture> has to be promoted before the img itself is touched.
    if (parent && parent.tagName === 'PICTURE') {
        parent.querySelectorAll('source[data-srcset]').forEach(source => {
            if (!source.getAttribute('srcset')) {
                source.setAttribute('srcset', source.getAttribute('data-srcset'));
            }
        });
    }

    // "auto" is lazysizes' stand-in for the width the element is laid out at.
    // The string itself is not a usable sizes value, so substitute the rendered
    // width once the image has a real box. Leave sizes unset when it does not,
    // rather than copying "auto" through.
    if (sizes && !img.getAttribute('sizes')) {
        let resolved = sizes;

        if (sizes === 'auto') {
            const rect = img.getBoundingClientRect();
            resolved = rect.width > 2 && rect.height > 2
                ? Math.round(rect.width) + 'px'
                : '';
        }

        if (resolved) {
            img.setAttribute('sizes', resolved);
        }
    }

    // Must precede src, otherwise the browser commits to src before it can pick
    // a candidate out of the set.
    if (srcset && !img.getAttribute('srcset')) {
        img.setAttribute('srcset', srcset);
    }

    if (src) {
        img.setAttribute('src', src);
    }

    img.classList.remove(LAZY_CLASS);
    img.classList.add(LOADED_CLASS);
}

function onIntersect(entries) {
    entries.forEach(entry => {
        if (!entry.isIntersecting) {
            return;
        }

        // A carousel slide parked flush against its clip edge counts as
        // intersecting even though none of it is on screen. Skipping those
        // leaves the image to the 0.01 threshold, which fires once the slide is
        // actually swiped in.
        if (entry.intersectionRect.width < 2 || entry.intersectionRect.height < 2) {
            return;
        }

        imageObserver.unobserve(entry.target);
        reveal(entry.target);
    });
}

/**
 * Observes any lazy images inside a node that was just added to the document.
 * Re-observing an element the observer already holds is a no-op, so no
 * bookkeeping is needed to avoid duplicates.
 *
 * @param {Node} node newly inserted node
 */
function observeWithin(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
    }

    if (node.matches(LAZY_SELECTOR)) {
        imageObserver.observe(node);
    }

    node.querySelectorAll(LAZY_SELECTOR).forEach(img => imageObserver.observe(img));
}

function onMutations(records) {
    records.forEach(record => record.addedNodes.forEach(observeWithin));
}

function start() {
    if (!('IntersectionObserver' in window)) {
        document.querySelectorAll(LAZY_SELECTOR).forEach(reveal);
        return;
    }

    imageObserver = new IntersectionObserver(onIntersect, {
        rootMargin: ROOT_MARGIN,
        // The second entry is what guarantees a follow-up callback for the
        // zero-area intersections onIntersect declines to act on.
        threshold: [0, 0.01]
    });

    document.querySelectorAll(LAZY_SELECTOR).forEach(img => imageObserver.observe(img));

    // Markup injected later (minicart, quickview, carousels, upsell tiles) is
    // picked up from the inserted subtrees rather than by rescanning the document.
    new MutationObserver(onMutations).observe(document.body, {
        childList: true,
        subtree: true
    });
}

/**
 * Initialises deferred image loading for `img.lazy-loading` elements. Safe to
 * call more than once; only the first call takes effect.
 */
export default function initLazyLoad() {
    if (started) {
        return;
    }

    started = true;

    if (document.body) {
        start();
    } else {
        document.addEventListener('DOMContentLoaded', start, { once: true });
    }
}
